import { ref } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import { useDebouncedSearchQuery } from "@utils/composables/filters/useDebouncedSearchQuery";
import type { ChatPage } from "@/types";
import { captureEvent } from "@/lib/analytics";

export type ChatPagePayload = Partial<Omit<ChatPage, "accounts">> & {
  account_ids?: number[];
  default_account_ids?: number[];
};
export type ChatPageCustomisation = Record<string, unknown>;

export const useChatPagesStore = defineStore("chatPages", () => {
  const chatPages = ref<ChatPage[]>([]);
  const chatPagesCount = ref(0);
  const currentPage = ref(1);
  const searchQuery = ref("");
  const sortColumn = ref("created_at");
  const sortDirection = ref<"asc" | "desc">("desc");

  // Customizer: the page being themed and the theme the embedded chat last published.
  const customizedChatPage = ref<ChatPage | null>(null);
  const previewCustomisation = ref<ChatPageCustomisation | null>(null);
  const savingCustomisation = ref(false);
  // Bumped to remount the embedded chat, which reloads the saved theme.
  const reloadKey = ref(0);

  const fetchChatPages = async (page: number | null = null) => {
    const params = new URLSearchParams();
    if (page) {
      params.append("page", String(page));
      if (searchQuery.value.trim()) params.append("search", searchQuery.value.trim());
      params.append("sort_column", sortColumn.value);
      params.append("sort_direction", sortDirection.value);
    }
    const query = params.toString();
    const { data } = await useMyFetch(`/chat_pages${query ? `?${query}` : ""}`).json<{
      chat_pages: ChatPage[];
      chat_pages_count: number;
    }>();
    chatPages.value = data.value?.chat_pages || [];
    chatPagesCount.value = data.value?.chat_pages_count || 0;
    if (page) currentPage.value = page;
    return chatPages.value;
  };

  // Scoped by policy, unlike the public token endpoint the chat uses: an
  // out-of-scope token 404s instead of returning the page.
  const fetchChatPageByToken = async (token: string) => {
    const { data, error } = await useMyFetch(`/chat_pages/by_token/${encodeURIComponent(token)}`).json<ChatPage>();
    // A 404 still fills `data` with the error body, so the verdict is `error`.
    customizedChatPage.value = error.value ? null : (data.value ?? null);
    previewCustomisation.value = null;
    return customizedChatPage.value;
  };

  const saveCustomisation = async () => {
    if (!customizedChatPage.value || !previewCustomisation.value) return null;

    savingCustomisation.value = true;
    try {
      const updated = await updateChatPage(customizedChatPage.value.id, {
        customisation: previewCustomisation.value,
      });
      if (updated) customizedChatPage.value = updated;
      return updated;
    } finally {
      savingCustomisation.value = false;
    }
  };

  const resetCustomisation = () => {
    previewCustomisation.value = null;
    reloadKey.value += 1;
  };

  const resetCustomizer = () => {
    customizedChatPage.value = null;
    previewCustomisation.value = null;
  };

  const createChatPage = async (payload: ChatPagePayload) => {
    const { data, error } = await useMyFetch("/chat_pages").post({ chat_page: payload }).json<ChatPage>();
    if (error.value) throw error.value;
    captureEvent("chat_page_created", { chat_page_id: data.value?.id });

    if (data.value) {
      chatPages.value.push(data.value);
      chatPages.value.sort((a, b) => a.name.localeCompare(b.name));
    }
    return data.value;
  };

  const updateChatPage = async (id: number, payload: ChatPagePayload) => {
    const { data, error } = await useMyFetch(`/chat_pages/${id}`).put({ chat_page: payload }).json<ChatPage>();
    if (error.value) throw error.value;
    captureEvent("chat_page_updated", { chat_page_id: id });

    if (data.value) {
      const index = chatPages.value.findIndex((p) => p.id === id);
      if (index !== -1) chatPages.value[index] = data.value;
      chatPages.value.sort((a, b) => a.name.localeCompare(b.name));
    }
    return data.value;
  };

  const deleteChatPage = async (id: number) => {
    const { error } = await useMyFetch(`/chat_pages/${id}`).delete();
    if (error.value) throw error.value;
    captureEvent("chat_page_deleted", { chat_page_id: id });

    chatPages.value = chatPages.value.filter((p) => p.id !== id);
  };

  const resetAllFilters = () => {
    resetSearchQuery();
    sortColumn.value = "created_at";
    sortDirection.value = "desc";
    currentPage.value = 1;
    chatPages.value = [];
    chatPagesCount.value = 0;
  };

  const { resetSearchQuery } = useDebouncedSearchQuery(searchQuery, () => fetchChatPages(1));

  return {
    chatPages,
    chatPagesCount,
    currentPage,
    searchQuery,
    sortColumn,
    sortDirection,
    fetchChatPages,
    resetAllFilters,
    createChatPage,
    updateChatPage,
    deleteChatPage,
    customizedChatPage,
    previewCustomisation,
    savingCustomisation,
    reloadKey,
    fetchChatPageByToken,
    saveCustomisation,
    resetCustomisation,
    resetCustomizer,
  };
});
